import React from 'react';
import { Box, Paper, Stack, Typography, alpha } from '@mui/material';
import { fmtMetricValue, fmtPercent } from './formatters';
import StatusChip, { STATUS_COLORS } from './StatusChip';
import type { ChainStage } from './types';

export interface StageDetailStripProps {
  stage: ChainStage;
  comparisonLabel: string;
  currencySymbol?: string;
}

/** Expands under a `ChainView` when a stage card is clicked — the minimum
 * "why did you color it that way" detail: the exact current/prior values
 * and the comparison window behind the delta. Generic across any monitor;
 * doesn't assume Lead-to-Cash's richer breakdown/movers/trend drill-down. */
export default function StageDetailStrip({ stage, comparisonLabel, currencySymbol = '$' }: StageDetailStripProps) {
  const { metric } = stage;
  const color = STATUS_COLORS[stage.status];
  const hasComparison = metric.value != null && metric.prior_value != null;

  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: 1.75,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        borderLeft: `3px solid ${color}`,
        bgcolor: alpha(color, 0.04),
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {stage.order}. {stage.name} — {metric.label}
        </Typography>
        <StatusChip status={stage.status} />
      </Stack>
      {hasComparison ? (
        <Typography variant="body2" color="text.secondary">
          {fmtMetricValue(metric, currencySymbol)} this period vs.{' '}
          {fmtMetricValue({ value: metric.prior_value, format: metric.format }, currencySymbol)} prior — a{' '}
          <Box component="span" fontWeight={700} color={metric.sentiment === 'bad' ? 'error.main' : metric.sentiment === 'good' ? 'success.main' : 'text.primary'}>
            {fmtPercent(metric.pct_change, true)}
          </Box>{' '}
          change ({comparisonLabel}).
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {fmtMetricValue(metric, currencySymbol)} this period — no prior-period value to compare ({comparisonLabel}).
        </Typography>
      )}
    </Paper>
  );
}
